import React, { Fragment, useEffect, useState } from 'react';
import { Check, ChevronDown, HelpCircle } from 'react-feather';
import { Combobox, Transition } from '@headlessui/react';
import TooltipWrapped from 'components/TippyTooltip';

function classNames(...classes) {
    return classes.filter(Boolean).join(' ');
}

interface AutocompleteOption {
    id: number | string;
    name: string;
    value?: number | string;
}

interface AutocompleteSelectFieldProps {
    options: AutocompleteOption[];
    name: string;
    label: string;
    handleChange: (option: AutocompleteOption) => void;
    helperText?: string;
    tooltip?: string;
    selectedOption?: AutocompleteOption;
}

export default function AutocompleteFormikField({
    options,
    name,
    label,
    handleChange,
    helperText,
    tooltip,
    selectedOption: preSelectedOption
}: AutocompleteSelectFieldProps) {
    const [query, setQuery] = useState('');
    const [selectedOption, setSelectedOption] = useState(preSelectedOption);

    useEffect(() => {
        if (selectedOption) {
            handleChange(selectedOption);
        }
    }, [selectedOption]);

    useEffect(() => {
        if (preSelectedOption) {
            setSelectedOption(preSelectedOption);
        }
    }, [preSelectedOption]);

    const filteredOptions =
        query === ''
            ? options
            : options.filter((option) => {
                  return option.name
                      .toLowerCase()
                      .replace(/\s+/g, '')
                      .includes(query.toLowerCase().replace(/\s+/g, ''));
              });

    return (
        <Combobox as="div" value={selectedOption} onChange={setSelectedOption}>
            <Combobox.Label className="block pl-4 text-brand font-semibold flex items-center gap-2">
                <span>{label}</span>
                {tooltip && (
                    <span>
                        <TooltipWrapped title={tooltip}>
                            <HelpCircle />
                        </TooltipWrapped>
                    </span>
                )}
            </Combobox.Label>
            <div className="relative mt-1">
                <Combobox.Input
                    placeholder={'Vpiši ime občine'}
                    name={name}
                    className="rounded-full border border-gray-500 py-2 px-4 shadow-sm focus:outline-none focus:ring-brand focus:ring-2 w-full whitespace-nowrap overflow-hidden text-ellipsis"
                    onChange={(event) => setQuery(event.target.value)}
                    displayValue={(option: AutocompleteOption) => option?.name}
                />
                <Combobox.Button className="absolute inset-y-0 right-0 flex items-center rounded-r-md px-2 focus:outline-none">
                    <ChevronDown className="h-5 w-5 text-gray-400" aria-hidden="true" />
                </Combobox.Button>

                <Transition
                    as={Fragment}
                    leave="transition ease-in duration-100"
                    leaveFrom="opacity-100"
                    leaveTo="opacity-0"
                    afterLeave={() => setQuery('')}
                >
                    <Combobox.Options className="absolute rounded-2xl z-10 mt-1 max-h-60 w-full overflow-auto bg-white py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm">
                        {filteredOptions.length === 0 && query !== '' ? (
                            <div className="relative cursor-default select-none py-2 px-4 text-gray-500">
                                Ni zadetkov
                            </div>
                        ) : (
                            filteredOptions.map((option: AutocompleteOption) => (
                                <Combobox.Option
                                    key={option.id}
                                    value={option}
                                    className={({ active }) =>
                                        classNames(
                                            'relative cursor-default select-none py-2 pl-3 pr-9',
                                            active ? 'bg-brand text-white' : 'text-primary'
                                        )
                                    }
                                >
                                    {({ active, selected }) => (
                                        <>
                                            <span className={classNames('block truncate', selected && 'font-semibold')}>
                                                {option.name}
                                            </span>

                                            {selected && (
                                                <span
                                                    className={classNames(
                                                        'absolute inset-y-0 right-0 flex items-center pr-4',
                                                        active ? 'text-white' : 'text-primary'
                                                    )}
                                                >
                                                    <Check className="h-5 w-5" aria-hidden="true" />
                                                </span>
                                            )}
                                        </>
                                    )}
                                </Combobox.Option>
                            ))
                        )}
                    </Combobox.Options>
                </Transition>
            </div>
            {helperText && <div className={`pl-4 mt-2 text-xs text-left text-secondary`}>{helperText}</div>}
        </Combobox>
    );
}
